import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, aeoGeo } from "@/lib/seo/schema";
import { buildRelatedHubs } from "@/lib/seo/related-hubs";
import { getCity } from "@/lib/seo/cities";
import { umeaTrust, umeaProductionPricing, umeaProcess } from "@/content/landing/umea";

const FAQ: LandingFAQ[] = [
  {
    "q": "Vad är skillnaden mellan filmproduktion och en filmbyrå?",
    "a": "Filmproduktion är hantverket. Filmbyrå inkluderar även strategi och kreativ idé. Fenny levererar båda i samma fasta pris."
  },
  {
    "q": "Hur ser en typisk filmproduktion i Umeå ut?",
    "a": "Brief (v 1) → manus & storyboard (v 2) → pre-prod (v 3) → inspelning 1–3 dagar (v 3–4) → klipp, grade, ljudmix (v 4–6) → leverans."
  },
  {
    "q": "Vad kostar filmproduktion i Umeå 2026?",
    "a": "Social-content: 40 000–120 000 kr. Reklamfilm 15–30 s: 150 000–450 000 kr. Brand-/industrifilm: 250 000–700 000 kr. Pulse: från 14 995 kr/mån."
  },
  {
    "q": "Hur snabbt kan ni vara igång?",
    "a": "Första möte inom 5 arbetsdagar. Pulse: första klipp inom 10–14 dagar."
  },
  {
    "q": "Var i Västerbotten filmar ni?",
    "a": "Hela regionen — Umeå universitet, SLU, Umeå Science Park, NUS, Holmsund och Sandviken samt Holmsund, Vännäs, Robertsfors och hela Västerbotten. Drönar-tillstånd för aktuella zoner."
  },
  {
    "q": "Vilken utrustning använder ni?",
    "a": "Sony FX6/FX9 eller ARRI Alexa Mini. Aputure/ARRI-ljus. DJI-drönare. Post i DaVinci Resolve och Premiere."
  },
  {
    "q": "Klarar ni krävande industri- och säkerhetsmiljöer?",
    "a": "Ja. Vana vid tech, forskning och skogsindustri. PPE, NDA, säkerhetsbriefer och samordning med skiftledare."
  },
  {
    "q": "Tar ni hand om manus och regi?",
    "a": "Ja. Vana att översätta komplex verksamhet till säljande film."
  },
  {
    "q": "Hur många versioner ingår?",
    "a": "En master per format. Engelska versioner för export-/forskningskommunikation vid behov."
  },
  {
    "q": "Får vi raw-materialet?",
    "a": "Standard: mastrade filer + stillbilder. Raw lagras 12 månader."
  },
  {
    "q": "Kan vi vara med på set?",
    "a": "Ja. Live-feed via Frame.io för distans-kunder."
  },
  {
    "q": "Hur fungerar betalning?",
    "a": "50% vid produktionsstart, 50% vid leverans."
  }
];

const CITY = getCity("umea")!;
const RELATED = buildRelatedHubs(CITY, "filmbyra");

export const Route = createFileRoute("/filmbyra_/umea")({
  head: () => ({
    meta: [
      { title: "Filmbyrå Umeå — produktion i Västerbotten | Fenny" },
      { name: "description", content: "Filmbyrå för Umeå och Holmsund, Vännäs, Robertsfors och hela Västerbotten. Strategi, produktion och versionering — fast pris, 4–6 v." },
      { property: "og:title", content: "Filmbyrå Umeå — Fenny" },
      { property: "og:description", content: "Filmbyrå för Umeå och Västerbotten." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/filmbyra/umea" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmbyra/umea" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmbyra/umea", name: "Filmbyrå Umeå — produktion i Västerbotten | Fenny", description: "Filmbyrå för Umeå och Holmsund, Vännäs, Robertsfors och hela Västerbotten. Strategi, produktion och versionering — fast pris, 4–6 v."})) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Umeå", path: "/filmbyra/umea", description: "Filmbyrå för Umeå och Västerbotten. Strategi, produktion och versionering.", postalRegion: "Västerbottens län" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Filmbyrå", path: "/filmbyra" }, { name: "Umeå", path: "/filmbyra/umea" }])) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      eyebrow="Filmbyrå · Umeå"
      title={<>Filmbyrån som faktiskt förstår Umeå och Västerbotten.</>}
      lede="Fenny är en filmbyrå med produktionsteam som producerar i Umeå och Holmsund, Vännäs, Robertsfors och hela Västerbotten. Vi producerar reklamfilm, brand storytelling, företagsfilm och employer branding för varumärken inom tech, forskning (UmU/SLU), skogsindustri och hälsosektorn (NUS)."
      tldr={<p>Fenny är filmbyrån för <strong>Umeå</strong>. Vi producerar i Holmsund, Vännäs, Robertsfors och hela Västerbotten. <strong>Fast pris</strong>, 4–6 v.</p>}
      ctaPrimary={{ label: "Få offert inom 48 h", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="filmbyra-umea"
      trust={umeaTrust}
      bullets={[
        { title: "Lokalt fokus", body: "Vi producerar i Holmsund, Vännäs, Robertsfors och hela Västerbotten. Resa inom regionen inkluderat." },
        { title: "Västerbotten-vana", body: "Djup erfarenhet av tech, forskning och skogsindustri." },
        { title: "Hela kedjan inhouse", body: "Strategi, regi, foto, ljud, klipp, grade." },
      ]}
      pricing={{ heading: "Vad det kostar", intro: "Riktpriser för 2026.", rows: umeaProductionPricing, footnote: "Alla priser exkl. moms." }}
      process={{ heading: "Från brief till leverans", intro: "Linjär process med skriftligt godkännande.", steps: umeaProcess }}
      sections={[
        localProductionSection("umea", "Umeå"),
        { id: "fenny-i-staden", heading: "Fenny i Umeå", body: <p>Vi jobbar regelbundet i Umeå. Skarp förplanering, koncentrerad inspelning och möjlighet att kombinera Umeå med Holmsund, Vännäs, Robertsfors och hela Västerbotten i samma produktion.</p> },
        { id: "vem", heading: "Vem vi passar för i Västerbotten", body: <p>Vi producerar för varumärken inom tech, forskning (UmU/SLU), skogsindustri och hälsosektorn (NUS) — och levererar <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">employer branding</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kundcase-film">kundcase-film</a>.</p> },
        { id: "modeller", heading: "Hur vi levererar", body: <p>Pulse-abonnemang för löpande output, Prime för enskilda större produktioner. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">Räkna pris</a>.</p> },
      ]}
      faq={FAQ}
      caseLimit={6}
      related={{
        eyebrow: "Relaterat",
        title: <>Mer för {"Umeå"}-team</>,
        formats: RELATED.formats,
        siblings: RELATED.siblings,
        comparisons: RELATED.comparisons,
      }}
      closing={{ title: "Söker ni filmbyrå i Umeå?", body: "30 minuter där vi mappar er insats och nästa steg i Västerbotten.", primary: { label: "Boka strategimöte", to: "/kontakt" }, secondary: { label: "Räkna pris", to: "/priser" } }}
    />
  );
}
